import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import formatNumber from "../../../hooks/formatNumber";
import formatReadingTime from "../../../hooks/formatReadingTime";
import formatTotalReadingTime from "../../../hooks/formatTotalReadingTime";

// import { ReactComponent as ArrowDown } from "../../../assets/images/arrow-down.svg";
// import { ReactComponent as ArrowUp } from "../../../assets/images/arrow-up.svg";

// import { ReactComponent as EngagementIcon } from "../../../assets/images/engagement.svg";
// import { ReactComponent as ViewsIcon } from "../../../assets/images/views.svg";
import parseTimeString from "../../../hooks/parseTimeString";
import { getSingleMetric } from "../../../store/slices/metric";
import {formatInterval} from "../../../hooks/formatInterval";
import {IDashboardConfig, IMetricData} from "../../../constants/interfaces";
import {ensureType} from "../../../hooks/ensureType";
import formatPostedOnChannels from "../../../hooks/formatPostedOnChannels";
import formatReadDepth from "../../../hooks/formatReadDepth";

type Props = {
	cellId: number,
	width: number,
	small: boolean,
}

const getExtraInfo = (metric: string, widgetConfig: any, currentBrand: any) => {
	if (metric !== 'activeUsers') {
		const timeWindow = widgetConfig.time_window.replace(/360m/, "6h").replace(/720m/, "12h").replace(/1440m/, "24h");

		return (widgetConfig.time_window === "midnight") ? "Today" : "Last " + timeWindow;
	}

	if (widgetConfig.brands.length <= 1) {
		return "Last " + currentBrand.dashboardSettings.fastMinutes + "m";
	}

	return "";
};

const MetricWidget = ({cellId, width, small}: Props) => {
	const data = useSelector((state: {metric: { isLoading: boolean, data: IMetricData }}) => state.metric);

	const dispatch = useDispatch();
	const [metricData, setMetricData] = useState(data);

	const widgetConfig = useSelector((state: {dashboard: {data: IDashboardConfig}}) =>
		ensureType(state.dashboard.data.grid_config.find((p) => p.cell_id === cellId))
	);
	const brand = useSelector((state: {brandsettings: {data: any}}) => state.brandsettings.data);

	const title = widgetConfig.label;
	const subtitle = widgetConfig.subtitle ? widgetConfig.subtitle : null;
	const metric = widgetConfig.content_type;
	const refreshRate = parseTimeString(widgetConfig.refresh);
	const brands = widgetConfig.brands.join(',');
	const apiInterval = formatInterval(widgetConfig.time_window);
	const apiPubDateInterval = widgetConfig.pubdate_interval;
	const sections = widgetConfig.sections.join('$$');
	const eventFields = (widgetConfig.event_fields && widgetConfig.event_fields.length) > 0 ? widgetConfig.event_fields[0].name : '';
	const tags = widgetConfig.tags ? widgetConfig.tags.join('$$') : '';
	const userNeeds = widgetConfig.user_needs ? widgetConfig.user_needs.join('$$') : '';
	const conversions = widgetConfig.conversions ? widgetConfig.conversions.join('$$') : '';
	const isPaid = widgetConfig.paid_type;
	const videoFilter = widgetConfig.video_filter;
	const pageType = widgetConfig.page_type;
	const referrers = widgetConfig.referrers ? widgetConfig.referrers.join('$$') : '';
	const referrerTypes = widgetConfig.referrer_types ? widgetConfig.referrer_types.join('$$') : '';
	const { includeChannels, excludeChannels } = formatPostedOnChannels(widgetConfig.posted_on_channels);
	// initial first load
	useEffect(() => {
		dispatch(getSingleMetric(cellId, metric, brands, apiInterval, sections, tags, conversions, eventFields, isPaid, videoFilter, pageType, referrers, referrerTypes, includeChannels, excludeChannels, apiPubDateInterval, userNeeds));
	}, [dispatch, cellId, metric, brands, apiInterval, sections, tags, conversions, eventFields, isPaid, videoFilter, pageType, referrers, referrerTypes, includeChannels, excludeChannels, apiPubDateInterval, userNeeds]);

	// update data
	useEffect(() => {
		const interval = setInterval(() => {
			dispatch(getSingleMetric(cellId, metric, brands, apiInterval, sections, tags, conversions, eventFields, isPaid, videoFilter, pageType, referrers, referrerTypes, includeChannels, excludeChannels, apiPubDateInterval, userNeeds));
		}, refreshRate);
		return () => clearInterval(interval);
	}, [dispatch, refreshRate, cellId, metric, brands, apiInterval, sections, tags, conversions, eventFields, isPaid, videoFilter, pageType, referrers, referrerTypes, includeChannels, excludeChannels, apiPubDateInterval, userNeeds]);

	// get relevant data from state array
	useEffect(() => {
		setMetricData(data);
	}, [data, widgetConfig]);

	// const setBrand = () => {
	// 	if (widgetConfig.brands && widgetConfig.brands.length) {
	// 		return (
	// 			<div className="metric-widget__brands">
	// 				{widgetConfig.brands.map((brand, idx) => (
	// 					<div className="metric-widget-brand" key={idx}>
	// 						<img src={brand} alt="brand" />
	// 					</div>
	// 				))}
	// 			</div>
	// 		);
	// 	}
	// };

	// const setGraphic = () => {
	// 	switch (metric) {
	// 		case "facebookConsumptions":
	// 			return <EngagementIcon className="metric-widget__shape" />;
	// 		case "views":
	// 		case "pageviews":
	// 		case "webpageviews":
	// 		case "apppageviews":
	// 		case "videoPageViews":
	// 			return <ViewsIcon className="metric-widget__shape" />;
	// 		default:
	// 			return <div />;
	// 	}
	// };

	// const addArrow = () => {
	// 	if (metricData.data.trend >= 0) {
	// 		return <ArrowUp className="arrow" />;
	// 	} else {
	// 		return <ArrowDown className="arrow" />;
	// 	}
	// };
	const isLoaded = (metricData && metricData.data && Object.keys(metricData.data).length > 0)? true : false;
	if (!metricData) return <div>Loading...</div>;
	if (metricData.isLoading && !isLoaded) return <div>Loading...</div>;

	let trendHTML = (
		<div className="metric-widget__content__update">&nbsp;</div>
	);
	if (metricData.data.trend) {
		const updateIsNegative = metricData.data.trend < 0;
		const trendValue = `${Math.round(Math.abs(metricData.data.trend))}%`;
		const trendText = metricData.data.trendText || "";
		trendHTML = (
			<div
				className={[
					"metric-widget__content__update",
					updateIsNegative
						? "metric-widget__content__update--negative"
						: "metric-widget__content__update--positive",
				].join(" ")}
			>
				{/*{addArrow()}*/}
				<span className="metric-widget__content__update__trend-value">
					{trendValue}
				</span>
				<span className="metric-widget__content__update__trend-text">
					{trendText}
				</span>
			</div>
		);
	}

	let metricValue;

	switch (metric) {
		case "averageReadingTime":
			metricValue = formatReadingTime(metricData.data[cellId]);
			break;
		case "totalReadingTime":
			metricValue = formatTotalReadingTime(metricData.data[cellId]);
			break;
		case "averageReadDepth":
			metricValue = formatReadDepth(metricData.data[cellId]);
			break;
		case "averageScrollDepth":
			metricValue = formatReadDepth(metricData.data[cellId]);
			break;
		default:
			metricValue = formatNumber(metricData.data[cellId]);
			break;
	}

	return (
		<div className={`metric-widget metric-widget-width-${width}`}>
			<div className="metric-widget__heading">
				<span className="metric-widget__title">{title}</span>
				{
					subtitle && (
						<span className="metric-widget__subtitle">{subtitle}</span>
					)
				}
				<span className="metric-widget__extra-info">{getExtraInfo(metric, widgetConfig, brand)}</span>
			</div>

			<div className={`metric-widget__content ${small ? "small" : ''}`}>
				<div className={`metric-widget__content__heading ${small ? "small" : ''}`}>
					{metricValue}
				</div>
				{trendHTML}
			</div>
			{/*{setGraphic()}*/}
			{/*{setBrand()}*/}
		</div>
	);
};

export default MetricWidget;
